import { optionsOf, resolveKind, KIND } from './kinds.js'

/**
 * DS-2 §2.2 and §2.3 — how a section becomes screens.
 *
 * Two independent switches, and conflating them was the false binary DS-1
 * found: `griddable` decides WHERE THE OPTION LABELS LIVE, and it has nothing
 * to do with how many items fit a screen.
 *
 *   griddable: true   labels hoisted into one sticky shared header
 *   griddable: false  labels repeated inside each item block
 *
 * Layout — row or card — is decided PER SECTION from the worst case across
 * every format that section uses, never per format. Design caught this
 * re-deriving their own numbers: per format, EPDS splits, because nine of its
 * ten items have labels under 28 characters and item 6 ("Yes, most of the time
 * I haven't been able to cope at all", 56) does not. That would put two
 * different controls in front of a participant inside one instrument on one
 * screen, which is worse than either uniform choice.
 */

const ROW_MAX_OPTIONS = 7
const ROW_MAX_LABEL = 28

/** Row layout only when every format in the section can honestly take it. */
export function sectionLayout(items, formats) {
  let worstOptions = 0
  let worstLabel = 0
  for (const item of items ?? []) {
    const format = formats?.[item.response_format]
    const options = optionsOf(format)
    if (!options.length) continue
    worstOptions = Math.max(worstOptions, options.length)
    for (const option of options) {
      worstLabel = Math.max(worstLabel, String(option.label ?? '').length)
    }
  }
  if (!worstOptions) return 'card'
  return worstOptions <= ROW_MAX_OPTIONS && worstLabel <= ROW_MAX_LABEL ? 'row' : 'card'
}

/**
 * What one item costs a screen. A row of marks is cheap; a stack of option
 * cards costs one line per option, which is why item count is the wrong unit.
 */
export function itemCost(item, formats, layout) {
  const format = formats?.[item.response_format]
  const kind = resolveKind(format)
  if (kind === KIND.NUMERIC || kind === KIND.TIME) return 2
  const optionCount = optionsOf(format).length
  if (kind === KIND.BINARY_PLUS_TEXT) return 3 + optionCount
  return layout === 'row' ? 2 : 1 + optionCount
}

export const SCREEN_BUDGET = 12

/**
 * Split a section's items into screens.
 *
 * Stated as the spec states it, because two of design's own derivations
 * disagreed by one screen: the rule is normative and any total is illustrative.
 * Config order throughout — never re-sorted.
 *
 * @param {number|null} maxItems  null for day 0; 4 for a beep body (DS-4 §3.3)
 */
export function paginate(items, formats, { layout, budget = SCREEN_BUDGET, maxItems = null } = {}) {
  const list = items ?? []
  if (!list.length) return []
  const mode = layout ?? sectionLayout(list, formats)

  const screens = []
  let current = []
  let used = 0

  for (const item of list) {
    const cost = itemCost(item, formats, mode)
    if (!current.length) {
      current = [item]
      used = cost
    } else if (used + cost <= budget && (maxItems === null || current.length < maxItems)) {
      current.push(item)
      used += cost
    } else {
      screens.push(current)
      current = [item]
      used = cost
    }
  }
  if (current.length) screens.push(current)
  return screens
}

/**
 * A griddable section is ONE scrolling screen, never paginated: the sticky
 * header is what makes long scroll work, and DS-1 confirmed it holds at 20 and
 * 25 rows.
 */
export function screensFor(section, formats, options = {}) {
  const items = section?.items ?? []
  if (!items.length) return []
  if (section?.questionnaire?.griddable ?? section?.griddable) return [items]
  return paginate(items, formats, options)
}
